import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import {
  AdornedInput,
  Box,
  Checkbox,
  DropdownSelect,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
} from '@zillow/constellation-10';

import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserPropertyCriteriaWebApiHandler_get,
  useRentalManagerUserPropertyCriteriaWebApiHandler_set,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import numberUtils from '../../../utils/numberUtils';

import { LC_PET_POLICY_FORM_CONSTANTS, THREE_PLUS } from '../../../constants/ScreeningCriteriaConstants';
import Criterion from '../../../model/ListingCriteria/Criterion';

import {
  emitDogSizeAllowedEvent,
  emitNumberOfDogsAllowedEvent,
  EVENT_IDS,
} from '../../../analytics/events/screeningCriteria';
import { useAuth } from '../../AuthProvider';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type {
  UserPropertyCriteriaGetCriteriaWebDto,
  UserPropertyCriteriaSetRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

const { CAT_COUNT_OPTIONS, DOG_COUNT_OPTIONS, DOG_SIZE_OPTIONS, LABEL_TO_VALUE_MAP, VALUE_TO_LABEL_MAP } =
  LC_PET_POLICY_FORM_CONSTANTS;

const requiredFieldErrorText = 'Required field';

// Helper function to map values >=3 to "3+"
export const threeOrMoreMapper = (arg: string) => {
  if (!arg) {
    return arg;
  }

  const value = Number(arg);

  if (arg === THREE_PLUS || (value !== null && !Number.isNaN(value) && value < 3)) {
    return arg;
  } else if (value !== null && !Number.isNaN(value) && value >= 3) {
    return THREE_PLUS;
  }

  return arg;
};

const isSmallOrMediumDogSize = (dogSize: string) => dogSize === 'small' || dogSize === 'medium';

const buildDogSizeCriteria = (
  dogSize: string,
  dogCount: string,
  negotiable: boolean,
): Array<UserPropertyCriteriaSetRequestWebDto> => {
  const mappedCount = threeOrMoreMapper(dogCount);
  if (isSmallOrMediumDogSize(dogSize)) {
    return [
      Criterion({
        type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
        criterion: '0',
        negotiable,
        isEnabled: true,
      }) as UserPropertyCriteriaSetRequestWebDto,
      Criterion({
        type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
        criterion: mappedCount,
        negotiable,
        isEnabled: true,
      }) as UserPropertyCriteriaSetRequestWebDto,
    ];
  }
  return [
    Criterion({
      type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
      criterion: mappedCount,
      negotiable,
      isEnabled: true,
    }) as UserPropertyCriteriaSetRequestWebDto,
    Criterion({
      type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
      criterion: '0',
      negotiable,
      isEnabled: true,
    }) as UserPropertyCriteriaSetRequestWebDto,
  ];
};

const PetPolicyCriteriaFormField = () => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  // Fetch initial data
  const { data: listingCriteriaData } = useRentalManagerUserPropertyCriteriaWebApiHandler_get(
    { listingAlias },
    {
      enabled: Boolean(listingAlias),
      meta: {
        context: { api: 'rental-manager', details: 'get pet policy criteria' },
        tags: ['users-properties-criteria-get'],
      },
    },
  );

  const criteria = useMemo(() => listingCriteriaData?.criteria || [], [listingCriteriaData]);
  const isFeed = listingCriteriaData?.isFeed ?? false;

  // Extract initial values
  const initialValues = useMemo(() => {
    const petPolicyCriteria = criteria.filter((c: UserPropertyCriteriaGetCriteriaWebDto) =>
      c.type.includes('petPolicy'),
    );
    return {
      catCount:
        petPolicyCriteria.find(
          (c: UserPropertyCriteriaGetCriteriaWebDto) =>
            c.type === RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT,
        )?.criterion || '',
      dogCount:
        petPolicyCriteria.find(
          (c: UserPropertyCriteriaGetCriteriaWebDto) =>
            c.type === RentalManagerListingCriteriaType.PET_POLICY_DOG_COUNT,
        )?.criterion || '',
      dogSize:
        petPolicyCriteria.find(
          (c: UserPropertyCriteriaGetCriteriaWebDto) => c.type === RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE,
        )?.criterion || '',
      petDeposit: numberUtils.stripTrailingDecimalZeros(
        petPolicyCriteria.find(
          (c: UserPropertyCriteriaGetCriteriaWebDto) =>
            c.type === RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
        )?.criterion || '',
      ),
      petRent: numberUtils.stripTrailingDecimalZeros(
        petPolicyCriteria.find(
          (c: UserPropertyCriteriaGetCriteriaWebDto) => c.type === RentalManagerListingCriteriaType.PET_POLICY_PET_RENT,
        )?.criterion || '',
      ),
      negotiable: petPolicyCriteria[0]?.negotiable ?? false,
    };
  }, [criteria]);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyCriteriaWebApiHandler_set,
    skipToast: true,
    meta: {
      invalidates: ['users-properties-criteria-get'],
    },
  });

  // Required field validation state
  const [isCatCountEmpty, setIsCatCountEmpty] = useState(false);
  const [isDogCountEmpty, setIsDogCountEmpty] = useState(false);

  // Cat count field
  const catCountField = useFieldSave({
    fieldName: 'catCount',
    initialValue: initialValues.catCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const criterionData = Criterion({
        type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT,
        criterion: threeOrMoreMapper(value),
        negotiable: negotiableField.value as boolean,
        isEnabled: true,
      });

      return {
        getParams: { listingAlias },
        postData: [criterionData] as Array<UserPropertyCriteriaSetRequestWebDto>,
      };
    },
  });

  // Dog count field
  const dogCountField = useFieldSave({
    fieldName: 'dogCount',
    initialValue: initialValues.dogCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const postData: Array<UserPropertyCriteriaSetRequestWebDto> = buildDogSizeCriteria(
        dogSizeField.value || 'large',
        value,
        negotiableField.value as boolean,
      );

      return {
        getParams: { listingAlias },
        postData,
      };
    },
  });

  // Dog size field
  const dogSizeField = useFieldSave({
    fieldName: 'dogSize',
    initialValue: initialValues.dogSize,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      return {
        getParams: { listingAlias },
        postData: buildDogSizeCriteria(value, dogCountField.value, negotiableField.value as boolean),
      };
    },
  });

  // Pet deposit field
  const petDepositField = useFieldSave({
    fieldName: 'petPolicyPetDeposit',
    initialValue: initialValues.petDeposit,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const criterionData = Criterion({
        type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
        criterion: value,
        negotiable: negotiableField.value as boolean,
        isEnabled: true,
      });

      return {
        getParams: { listingAlias },
        postData: [criterionData] as Array<UserPropertyCriteriaSetRequestWebDto>,
      };
    },
  });

  // Pet rent field
  const petRentField = useFieldSave({
    fieldName: 'petPolicyPetRent',
    initialValue: initialValues.petRent,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const criterionData = Criterion({
        type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT,
        criterion: value,
        negotiable: negotiableField.value as boolean,
        isEnabled: true,
      });

      return {
        getParams: { listingAlias },
        postData: [criterionData] as Array<UserPropertyCriteriaSetRequestWebDto>,
      };
    },
  });

  // Negotiable field - updates ALL criteria
  const negotiableField = useFieldSave({
    fieldName: 'negotiable',
    initialValue: initialValues.negotiable,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const negotiable = Boolean(value);
      const allCriteria: Array<UserPropertyCriteriaSetRequestWebDto> = [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT,
          criterion: catCountField.value,
          negotiable,
          isEnabled: true,
        }) as UserPropertyCriteriaSetRequestWebDto,
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_DOG_COUNT,
          criterion: dogCountField.value,
          negotiable,
          isEnabled: true,
        }) as UserPropertyCriteriaSetRequestWebDto,
        ...buildDogSizeCriteria(dogSizeField.value, dogCountField.value, negotiable),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
          criterion: petDepositField.value,
          negotiable,
          isEnabled: true,
        }) as UserPropertyCriteriaSetRequestWebDto,
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT,
          criterion: petRentField.value,
          negotiable,
          isEnabled: true,
        }) as UserPropertyCriteriaSetRequestWebDto,
      ];

      return {
        getParams: { listingAlias },
        postData: allCriteria,
      };
    },
  });

  // Auto-set dog size to "large" when dog count changes from 0 to non-zero
  useEffect(() => {
    if (dogCountField.value !== '0' && !dogSizeField.value) {
      dogSizeField.onChange('large');
    }
  }, [dogCountField.value]);

  // Clear deposit/rent when both counts are 0
  useEffect(() => {
    const bothZero = catCountField.value === '0' && dogCountField.value === '0';
    if (bothZero) {
      if (petDepositField.value) petDepositField.onChange('');
      if (petRentField.value) petRentField.onChange('');
    }
  }, [catCountField.value, dogCountField.value]);

  // Required field validation
  useEffect(() => {
    if (isCatCountEmpty) {
      setIsCatCountEmpty(!catCountField.value);
    }
    if (isDogCountEmpty) {
      setIsDogCountEmpty(!dogCountField.value);
    }
  }, [catCountField.value, dogCountField.value, isCatCountEmpty, isDogCountEmpty]);

  const isDogSizeDisabled = isFeed || dogCountField.value === '0';
  const isDepositAndRentDisabled = catCountField.value === '0' && dogCountField.value === '0';

  const petPolicyDogSizeValue =
    VALUE_TO_LABEL_MAP[
      `${RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE}${threeOrMoreMapper(dogSizeField.value)}` as keyof typeof VALUE_TO_LABEL_MAP
    ] || '';

  return (
    <div data-testid="PetPolicyCriteriaFormField">
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gridTemplateRows: {
            base: 'repeat(5, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: '400',
        }}
      >
        {/* Cat count */}
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 12', lg: 'span 4' }, gridRow: '1' }}>
          <FormField
            data-testid="PetPolicyCriteriaFormField-Cats"
            disabled={isFeed}
            error={isCatCountEmpty}
            label={<Label required={!isFeed}>Cats</Label>}
            control={
              <DropdownSelect
                value={
                  VALUE_TO_LABEL_MAP[
                    `${RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT}${threeOrMoreMapper(catCountField.value)}` as keyof typeof VALUE_TO_LABEL_MAP
                  ] || ''
                }
                options={CAT_COUNT_OPTIONS}
                onChange={(value) => {
                  setIsCatCountEmpty(false);
                  catCountField.onChange(LABEL_TO_VALUE_MAP[value as keyof typeof LABEL_TO_VALUE_MAP]);
                }}
                disabled={isFeed}
              />
            }
            description={isCatCountEmpty ? <FormHelp>{requiredFieldErrorText}</FormHelp> : undefined}
          />
        </Box>

        {/* Dog count */}
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 4' },
            gridRow: '2',
          }}
        >
          <FormField
            data-testid="PetPolicyCriteriaFormField-Dogs"
            disabled={isFeed}
            error={isDogCountEmpty}
            label={<Label required={!isFeed}>Dogs</Label>}
            control={
              <DropdownSelect
                value={
                  VALUE_TO_LABEL_MAP[
                    `${RentalManagerListingCriteriaType.PET_POLICY_DOG_COUNT}${threeOrMoreMapper(dogCountField.value)}` as keyof typeof VALUE_TO_LABEL_MAP
                  ] || ''
                }
                options={DOG_COUNT_OPTIONS}
                onChange={(value) => {
                  setIsDogCountEmpty(false);
                  emitNumberOfDogsAllowedEvent({ currentProperty: listingCriteriaData as ZrmClickstreamListing, user });
                  dogCountField.onChange(LABEL_TO_VALUE_MAP[value as keyof typeof LABEL_TO_VALUE_MAP]);
                }}
                disabled={isFeed}
                data-eventid={EVENT_IDS.NUMBER_OF_DOGS_ALLOWED}
              />
            }
            description={<FormHelp>{isDogCountEmpty && requiredFieldErrorText}</FormHelp>}
          />
        </Box>

        {/* Dog size */}
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 8' },
            gridRow: { base: '3', lg: '2' },
          }}
        >
          <FormField
            data-testid="PetPolicyCriteriaFormField-DogSize"
            data-eventid={EVENT_IDS.DOG_SIZE_ALLOWED}
            label={<Label>Maximum dog size</Label>}
            disabled={isDogSizeDisabled}
            control={
              <DropdownSelect
                value={petPolicyDogSizeValue}
                placeholder="Select size"
                options={DOG_SIZE_OPTIONS}
                onChange={(value) => {
                  dogSizeField.onChange(LABEL_TO_VALUE_MAP[value as keyof typeof LABEL_TO_VALUE_MAP]);
                  emitDogSizeAllowedEvent({ currentProperty: listingCriteriaData as ZrmClickstreamListing, user });
                }}
                disabled={isDogSizeDisabled}
                data-eventid={EVENT_IDS.DOG_SIZE_ALLOWED}
              />
            }
          />
        </Box>

        {/* Pet deposit */}
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 6' },
            gridRow: { base: '4', lg: '3' },
          }}
        >
          <FormField
            data-testid="PetPolicyCriteriaFormField-Deposit"
            disabled={isFeed || isDepositAndRentDisabled}
            error={!!petDepositField.error}
            label={<Label>Pet deposit</Label>}
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    name="petDeposit"
                    value={String(petDepositField.value || '')}
                    onChange={(e) => petDepositField.onChange(e.target.value)}
                    onBlur={petDepositField.onBlur}
                    error={!!petDepositField.error}
                    disabled={isFeed || isDepositAndRentDisabled}
                  />
                }
              />
            }
            description={<FormHelp>{petDepositField.error || 'Set to $0 if not required'}</FormHelp>}
          />
        </Box>

        {/* Pet rent */}
        <Box
          css={{
            display: 'grid',
            gridColumnEnd: { base: 'span 12', lg: 'span 6' },
            gridRow: { base: '5', lg: '3' },
          }}
        >
          <FormField
            data-testid="PetPolicyCriteriaFormField-Rent"
            disabled={isFeed || isDepositAndRentDisabled}
            error={!!petRentField.error}
            label={<Label>Pet rent</Label>}
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    name="petRent"
                    value={String(petRentField.value || '')}
                    onChange={(e) => petRentField.onChange(e.target.value)}
                    onBlur={petRentField.onBlur}
                    disabled={isFeed || isDepositAndRentDisabled}
                  />
                }
                endAdornment={<AdornedInput.Adornment>/mo</AdornedInput.Adornment>}
              />
            }
            description={<FormHelp>{petRentField.error || 'Set to $0 if not required'}</FormHelp>}
          />
        </Box>
      </Box>

      {/* Negotiable checkbox */}
      <LabeledControl
        label={<Label>Let renters know this is negotiable</Label>}
        control={
          <Checkbox
            name="negotiable"
            checked={Boolean(negotiableField.value)}
            onChange={(e) => negotiableField.onChange(e.target.checked)}
            disabled={isFeed}
            data-testid="PetPolicyCriteriaFormField-NegotiableCheckbox"
          />
        }
      />
    </div>
  );
};

export default PetPolicyCriteriaFormField;
